import { useMemo } from 'react';
import ReactECharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import type { HoldingRevenueBreakdown } from '@monitoring-panel/types';
import { formatNumber } from '@/lib/utils';
import {
  ADVIA_CHART_OPTS,
  ADVIA_CHART_TOKENS,
  ADVIA_ECHARTS_THEME,
  CATEGORICAL_COLORS,
  tooltipRow,
  tooltipTitle,
} from '@/lib/advia-charts';

interface HoldingsSunburstChartProps {
  data: HoldingRevenueBreakdown[];
  showForecasted?: boolean;
}

export function HoldingsSunburstChart({ data, showForecasted = false }: HoldingsSunburstChartProps) {
  const chartOption: EChartsOption = useMemo(() => {
    if (!data || data.length === 0) return {};

    // Build hierarchical data structure for sunburst
    // Root > Holdings > Agencies > Accounts
    const treeData = data.map((holding) => ({
      name: holding.holding,
      value: showForecasted
        ? holding.revenue + holding.forecastedRevenue
        : holding.revenue,
      children: holding.agencies.map((agency) => ({
        name: agency.agency,
        value: showForecasted
          ? agency.revenue + agency.forecastedRevenue
          : agency.revenue,
        children: agency.accounts.map((account) => ({
          name: account.account,
          value: showForecasted
            ? account.revenue + account.forecastedRevenue
            : account.revenue,
        })),
      })),
    }));

    return {
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => {
          const name = params.name;
          const value = params.value;
          const percent = params.percent || 0;
          const treePathInfo = params.treePathInfo || [];

          let result = tooltipTitle(name);

          // Show hierarchy path
          if (treePathInfo.length > 1) {
            const path = treePathInfo.slice(1).map((item: any) => item.name).join(' → ');
            result += `<div style="margin:4px 0;padding:4px 0;border-bottom:1px solid var(--border-gray);color:var(--text-secondary);font-size:var(--fs-label)">${path}</div>`;
          }

          result += tooltipRow('Revenue', formatNumber(value, 'euro'));
          result += tooltipRow('Share', formatNumber(percent, 'percentage', 2));

          return result;
        },
      },
      color: [...CATEGORICAL_COLORS],
      series: [
        {
          type: 'sunburst',
          data: treeData,
          radius: ['20%', '95%'],
          itemStyle: {
            borderRadius: 4,
            borderWidth: 2,
            borderColor: ADVIA_CHART_TOKENS.ivory,
          },
          label: {
            rotate: 'radial',
            fontSize: 12,
            fontFamily: ADVIA_CHART_TOKENS.fontSans,
            color: ADVIA_CHART_TOKENS.textPrimary,
            fontWeight: 500,
            overflow: 'truncate',
            width: 100,
          },
          levels: [
            {},
            {
              // Holdings - innermost ring
              r0: '20%',
              r: '45%',
              itemStyle: {
                borderWidth: 3,
                borderColor: ADVIA_CHART_TOKENS.ivory,
              },
              label: {
                rotate: 0,
                fontSize: 12,
                fontWeight: 'bold',
                color: ADVIA_CHART_TOKENS.textPrimary,
                align: 'center',
                verticalAlign: 'middle',
                distance: 0,
                formatter: (params: any) => {
                  const name = params.name;
                  // Truncate long names
                  return name.length > 20 ? name.substring(0, 18) + '...' : name;
                },
              },
            },
            {
              // Agencies - middle ring
              r0: '45%',
              r: '70%',
              itemStyle: {
                borderWidth: 2,
              },
              label: {
                rotate: 'radial',
                fontSize: 11,
                fontWeight: 600,
                color: ADVIA_CHART_TOKENS.textPrimary,
                align: 'center',
                formatter: (params: any) => {
                  const name = params.name;
                  // Truncate long names
                  return name.length > 25 ? name.substring(0, 23) + '...' : name;
                },
              },
            },
            {
              // Accounts - outermost ring
              r0: '70%',
              r: '95%',
              label: {
                position: 'outside',
                padding: 4,
                fontSize: 11,
                fontWeight: 500,
                color: ADVIA_CHART_TOKENS.textSecondary,
                formatter: (params: any) => {
                  const name = params.name;
                  // Truncate long names for outer labels
                  return name.length > 30 ? name.substring(0, 28) + '...' : name;
                },
              },
              itemStyle: {
                borderWidth: 1,
              },
            },
          ],
          emphasis: {
            focus: 'ancestor',
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(41, 41, 41, 0.2)',
            },
            label: {
              fontSize: 12,
              fontWeight: 'bold',
            },
          },
        },
      ],
    };
  }, [data, showForecasted]);

  if (!data || data.length === 0) {
    return (
      <div className="adv-card adv-state font-adv-sans">
        <p className="adv-state__desc">No holdings data available</p>
      </div>
    );
  }

  return (
    <div className="adv-card p-6 font-adv-sans">
      <div className="mb-6">
        <div className="flex items-center justify-between gap-4 mb-2">
          <h2 className="adv-section-title">
            Revenue Distribution: Holdings → Agencies → Accounts
          </h2>
          <div className="adv-tag adv-tag--default">
            {showForecasted ? 'Total Revenue (Actual + Forecasted)' : 'Actual Revenue Only'}
          </div>
        </div>
        <p className="adv-section-desc">
          Interactive sunburst chart showing revenue hierarchy. Click on any segment to zoom in. Hover for details.
        </p>
      </div>
      <ReactECharts
        option={chartOption}
        style={{ height: 700 }}
        theme={ADVIA_ECHARTS_THEME}
        opts={ADVIA_CHART_OPTS}
      />
    </div>
  );
}
